import { Contract, formatEther, formatUnits, parseEther, parseUnits } from 'ethers';
import dataServiceExchangeArtifact from '../contracts/abis/DataServiceExchange.json';
import lbtArtifact from '../contracts/abis/LiveBetterToken.json';
import stakeholderArtifact from '../contracts/abis/StakeholderRegistry.json';
import dataAssetArtifact from '../contracts/abis/DataAssetRegistry.json';
import serviceRegistryArtifact from '../contracts/abis/ServiceRegistry.json';
import dataNotarizationArtifact from '../contracts/abis/DataNotarization.json';
import dsaArtifact from '../contracts/abis/DataServiceAgreement.json';
import eubsoArtifact from '../contracts/abis/EUBSOContribution.json';
import hubArtifact from '../contracts/abis/LiveBetterHub.json';
import { CONTRACT_ADDRESSES } from '../config/contracts';

const sanitizeText = (value) => String(value || '').replace(/[^\w:/#?&=.-]/g, '').trim();

const parseContractEvent = (contract, receipt, eventName) => {
  const parsedEvent = (receipt?.logs || [])
    .map((log) => {
      try {
        return contract.interface.parseLog(log);
      } catch {
        return null;
      }
    })
    .find((event) => event?.name === eventName);

  return {
    found: Boolean(parsedEvent),
    args: parsedEvent?.args ?? null,
  };
};

const buildValidatedWriteResult = ({
  tx,
  receipt,
  contract,
  eventName,
  requireEvent = false,
  mapEventArgs,
}) => {
  const status = receipt?.status === 1 || receipt?.status === 1n ? 'confirmed' : 'failed';
  if (status !== 'confirmed') {
    throw new Error('Blockchain transaction was not confirmed');
  }

  const event = eventName ? parseContractEvent(contract, receipt, eventName) : { found: false, args: null };
  if (requireEvent && !event.found) {
    throw new Error(`Expected ${eventName} event was not found in receipt logs`);
  }

  return {
    txHash: receipt?.hash || tx.hash,
    blockNumber: receipt?.blockNumber ?? null,
    status,
    validation: {
      confirmed: true,
      eventName: eventName || null,
      eventFound: Boolean(event.found),
    },
    ...(event.found && typeof mapEventArgs === 'function' ? mapEventArgs(event.args) : {}),
  };
};

const ensureAddress = (address, label) => {
  if (!address) {
    throw new Error(`${label} address is not configured`);
  }
  return address;
};

const hasDeployedCode = async (provider, address) => {
  if (!provider || typeof provider.getCode !== 'function') return false;
  const code = await provider.getCode(address);
  return Boolean(code && code !== '0x');
};

/**
 * Resolves the provider from a signer or provider and verifies the contract
 * has deployed bytecode at the given address.  Throws a user-friendly error
 * when the contract is missing (e.g. after a Hardhat node restart).
 */
const requireDeployedContract = async (signerOrProvider, address, label) => {
  const provider =
    signerOrProvider?.provider ?? signerOrProvider;
  const deployed = await hasDeployedCode(provider, address);
  if (!deployed) {
    throw new Error(
      `${label} contract is not deployed at ${address}. ` +
      'The blockchain node may have restarted — redeploy the contracts and run export-to-hub.js.'
    );
  }
};

/* ─── Legacy contract getter (DataServiceExchange) ─── */

export const getDataServiceExchangeContract = (signerOrProvider) =>
  new Contract(
    ensureAddress(CONTRACT_ADDRESSES.DataServiceExchange, 'DataServiceExchange'),
    dataServiceExchangeArtifact.abi,
    signerOrProvider
  );

/* ─── Core LiveBetter contract getters ─── */

export const getLBTContract = (signerOrProvider) =>
  new Contract(
    ensureAddress(CONTRACT_ADDRESSES.LiveBetterToken, 'LiveBetterToken'),
    lbtArtifact.abi,
    signerOrProvider
  );

export const getStakeholderRegistryContract = (signerOrProvider) =>
  new Contract(
    ensureAddress(CONTRACT_ADDRESSES.StakeholderRegistry, 'StakeholderRegistry'),
    stakeholderArtifact.abi,
    signerOrProvider
  );

export const getDataAssetRegistryContract = (signerOrProvider) =>
  new Contract(
    ensureAddress(CONTRACT_ADDRESSES.DataAssetRegistry, 'DataAssetRegistry'),
    dataAssetArtifact.abi,
    signerOrProvider
  );

export const getServiceRegistryContract = (signerOrProvider) =>
  new Contract(
    ensureAddress(CONTRACT_ADDRESSES.ServiceRegistry, 'ServiceRegistry'),
    serviceRegistryArtifact.abi,
    signerOrProvider
  );

export const getDataNotarizationContract = (signerOrProvider) =>
  new Contract(
    ensureAddress(CONTRACT_ADDRESSES.DataNotarization, 'DataNotarization'),
    dataNotarizationArtifact.abi,
    signerOrProvider
  );

export const getDataServiceAgreementContract = (signerOrProvider) =>
  new Contract(
    ensureAddress(CONTRACT_ADDRESSES.DataServiceAgreement, 'DataServiceAgreement'),
    dsaArtifact.abi,
    signerOrProvider
  );

export const getEUBSOContributionContract = (signerOrProvider) =>
  new Contract(
    ensureAddress(CONTRACT_ADDRESSES.EUBSOContribution, 'EUBSOContribution'),
    eubsoArtifact.abi,
    signerOrProvider
  );

export const getLiveBetterHubContract = (signerOrProvider) =>
  new Contract(
    ensureAddress(CONTRACT_ADDRESSES.LiveBetterHub, 'LiveBetterHub'),
    hubArtifact.abi,
    signerOrProvider
  );

export const mapOffer = (offer) => ({
  id: Number(offer.id ?? offer.offerId ?? 0),
  provider: offer.provider,
  dataType: offer.dataType,
  // ipfsCid is the canonical field; dataHash kept as alias for backward compatibility
  ipfsCid: offer.ipfsCid ?? offer.dataHash ?? '',
  dataHash: offer.ipfsCid ?? offer.dataHash ?? '',
  // Guard against undefined/null price (stale ABI mismatch would give undefined)
  priceWei: offer.price != null ? String(offer.price) : '0',
  priceEth: formatEther(offer.price ?? 0n),
  validUntil: Number(offer.validUntil),
  isActive: Boolean(offer.isActive),
  // Rich metadata fields — now stored fully on-chain
  country: offer.country ?? '',
  buildingTypology: offer.buildingTypology ?? '',
  description: offer.description ?? '',
  buildingId: offer.buildingId ?? '',
  // Expose metadata as a nested object so DataMarketplace filter logic works unchanged
  metadata: {
    country: offer.country ?? '',
    typology: offer.buildingTypology ?? '',
  },
});

export const fetchOnChainOffers = async (provider) => {
  if (!CONTRACT_ADDRESSES.DataServiceExchange) return [];
  const contractAddress = ensureAddress(CONTRACT_ADDRESSES.DataServiceExchange, 'DataServiceExchange');
  const hasCode = await hasDeployedCode(provider, contractAddress);
  if (!hasCode) {
    return [];
  }
  const contract = getDataServiceExchangeContract(provider);
  // Prefer getActiveOffers() (new contract) — returns only live, non-expired offers.
  // Fall back to getOffers() + client-side filtering when the old contract is still deployed.
  let raw;
  try {
    raw = await contract.getActiveOffers();
  } catch {
    const all = await contract.getOffers();
    const now = Math.floor(Date.now() / 1000);
    raw = all.filter((o) => Boolean(o.isActive) && Number(o.validUntil) >= now);
  }
  return raw.map(mapOffer);
};

export const acceptOfferOnChain = async ({ signer, offerId, priceWei }) => {
  if (!CONTRACT_ADDRESSES.DataServiceExchange) {
    return { txHash: null, blockNumber: null };
  }

  const addr = ensureAddress(CONTRACT_ADDRESSES.DataServiceExchange, 'DataServiceExchange');
  await requireDeployedContract(signer, addr, 'DataServiceExchange');

  // Validate and convert priceWei to BigInt before touching the contract.
  // BigInt() throws on undefined, null, empty string, or decimal strings —
  // catching here gives a clear error instead of a confusing RPC revert.
  let priceWeiBigInt;
  try {
    priceWeiBigInt = BigInt(priceWei);
  } catch {
    throw new Error(`Cannot accept offer: invalid price value "${priceWei}" — expected a wei integer string`);
  }
  if (priceWeiBigInt === 0n) {
    throw new Error('Cannot accept offer: price is zero');
  }

  const contract = getDataServiceExchangeContract(signer);
  // value must be a BigInt (wei) for ethers.js v6. Passing a string would
  // silently send 0 value in some ethers versions.
  const tx = await contract.acceptOffer(offerId, {
    value: priceWeiBigInt,
  });
  const receipt = await tx.wait();
  return buildValidatedWriteResult({
    tx,
    receipt,
    contract,
    eventName: 'OfferAccepted',
    requireEvent: true,
    mapEventArgs: (args) => ({ offerId: Number(args.offerId) }),
  });
};

export const createOfferOnChain = async ({
  signer,
  dataType,
  ipfsCid,
  priceEth,
  validUntilUnix,
  country = '',
  buildingTypology = '',
  description = '',
  buildingId = '',
}) => {
  if (!CONTRACT_ADDRESSES.DataServiceExchange) {
    throw new Error(
      'DataServiceExchange contract is not configured. Set REACT_APP_DATA_SERVICE_EXCHANGE_ADDRESS in frontend/.env and restart the dev server.'
    );
  }
  const exchangeAddr = ensureAddress(CONTRACT_ADDRESSES.DataServiceExchange, 'DataServiceExchange');
  await requireDeployedContract(signer, exchangeAddr, 'DataServiceExchange');
  const contract = getDataServiceExchangeContract(signer);
  const tx = await contract.createOffer(
    sanitizeText(dataType),
    sanitizeText(ipfsCid),
    parseEther(String(priceEth)),
    String(validUntilUnix),
    sanitizeText(country),
    sanitizeText(buildingTypology),
    String(description).trim().slice(0, 500),
    sanitizeText(buildingId)
  );
  const receipt = await tx.wait();
  return buildValidatedWriteResult({
    tx,
    receipt,
    contract,
    eventName: 'OfferCreated',
    requireEvent: true,
    mapEventArgs: (args) => ({ offerId: Number(args.offerId) }),
  });
};

export const notarizeHashOnChain = async ({ signer, hash, description = '' }) => {
  const notarizationAddr = ensureAddress(CONTRACT_ADDRESSES.DataNotarization, 'DataNotarization');
  await requireDeployedContract(signer, notarizationAddr, 'DataNotarization');
  const contract = getDataNotarizationContract(signer);
  // If the hash is already notarized the contract will revert with
  // "DataNotarization: hash exists". Check first and skip the tx so that
  // publishing an offer with a previously-used reference still works.
  const alreadyExists = await contract.verifyPoE(hash);
  if (alreadyExists) {
    return { txHash: null, blockNumber: null, alreadyNotarized: true };
  }
  const tx = await contract.notarizePoE(hash, description);
  const receipt = await tx.wait();
  return {
    ...buildValidatedWriteResult({
      tx,
      receipt,
      contract,
    }),
    alreadyNotarized: false,
  };
};

export const verifyHashOnChain = async ({ provider, hash }) => {
  const notarizationAddr = ensureAddress(CONTRACT_ADDRESSES.DataNotarization, 'DataNotarization');
  const deployed = await hasDeployedCode(provider, notarizationAddr);
  if (!deployed) return { exists: false, blockNumber: 0, timestamp: 0 };
  const contract = getDataNotarizationContract(provider);
  const exists = await contract.verifyPoE(hash);
  return { exists: Boolean(exists), blockNumber: 0, timestamp: 0 };
};

/* ─── LBT Token Functions ─── */

export const getLBTBalance = async (provider, address) => {
  if (!CONTRACT_ADDRESSES.LiveBetterToken) return '0';
  const tokenAddr = ensureAddress(CONTRACT_ADDRESSES.LiveBetterToken, 'LiveBetterToken');
  const deployed = await hasDeployedCode(provider, tokenAddr);
  if (!deployed) return '0';
  const contract = getLBTContract(provider);
  const balance = await contract.balanceOf(address);
  return formatUnits(balance, 18);
};

export const approveLBT = async ({ signer, spender, amount }) => {
  const tokenAddr = ensureAddress(CONTRACT_ADDRESSES.LiveBetterToken, 'LiveBetterToken');
  await requireDeployedContract(signer, tokenAddr, 'LiveBetterToken');
  const contract = getLBTContract(signer);
  const tx = await contract.approve(spender, parseUnits(String(amount), 18));
  const receipt = await tx.wait();
  return { txHash: receipt?.hash || tx.hash };
};

/* ─── Stakeholder Registry Functions ─── */

export const registerStakeholder = async ({ signer, roles, countryCode }) => {
  const stakeholderAddr = ensureAddress(CONTRACT_ADDRESSES.StakeholderRegistry, 'StakeholderRegistry');
  await requireDeployedContract(signer, stakeholderAddr, 'StakeholderRegistry');
  const contract = getStakeholderRegistryContract(signer);
  const tx = await contract.register(roles, countryCode);
  const receipt = await tx.wait();
  return { txHash: receipt?.hash || tx.hash };
};

export const getStakeholderInfo = async (provider, address) => {
  if (!CONTRACT_ADDRESSES.StakeholderRegistry) return null;
  const stakeholderAddr = ensureAddress(CONTRACT_ADDRESSES.StakeholderRegistry, 'StakeholderRegistry');
  const deployed = await hasDeployedCode(provider, stakeholderAddr);
  if (!deployed) return null;
  const contract = getStakeholderRegistryContract(provider);
  const info = await contract.getStakeholder(address);
  return {
    registered: Boolean(info.registered),
    verified: Boolean(info.verified),
    roles: Number(info.roles),
    gdprConsentTimestamp: Number(info.gdprConsentTimestamp),
    countryCode: info.countryCode,
    registeredAt: Number(info.registeredAt),
  };
};

/* ─── Data Asset Registry Functions ─── */

export const registerDataAsset = async ({
  signer,
  dataType,
  contentHash,
  metadataURI,
  buildingId,
  coverageStart,
  coverageEnd,
  priceInLBT,
}) => {
  const assetAddr = ensureAddress(CONTRACT_ADDRESSES.DataAssetRegistry, 'DataAssetRegistry');
  await requireDeployedContract(signer, assetAddr, 'DataAssetRegistry');
  const contract = getDataAssetRegistryContract(signer);
  const tx = await contract.registerAsset(
    dataType,
    contentHash,
    metadataURI,
    buildingId,
    coverageStart,
    coverageEnd,
    parseUnits(String(priceInLBT), 18)
  );
  const receipt = await tx.wait();
  return buildValidatedWriteResult({
    tx,
    receipt,
    contract,
    eventName: 'AssetRegistered',
    requireEvent: true,
    mapEventArgs: (args) => ({ assetId: Number(args.assetId) }),
  });
};

export const getDataAsset = async (provider, assetId) => {
  const assetAddr = ensureAddress(CONTRACT_ADDRESSES.DataAssetRegistry, 'DataAssetRegistry');
  await requireDeployedContract(provider, assetAddr, 'DataAssetRegistry');
  const contract = getDataAssetRegistryContract(provider);
  const asset = await contract.getAsset(assetId);
  return {
    assetId: Number(asset.assetId),
    provider: asset.provider,
    dataType: Number(asset.dataType),
    contentHash: asset.contentHash,
    metadataURI: asset.metadataURI,
    buildingId: asset.buildingId,
    coverageStart: Number(asset.coverageStart),
    coverageEnd: Number(asset.coverageEnd),
    priceInLBT: formatUnits(asset.priceInLBT, 18),
    qualityScore: Number(asset.qualityScore),
    euBsoEligible: Boolean(asset.euBsoEligible),
    active: Boolean(asset.active),
    registeredAt: Number(asset.registeredAt),
  };
};

/* ─── Data Service Agreement Functions ─── */

export const proposeAgreement = async ({
  signer,
  dataAssetId,
  serviceId,
  termsHash,
  euBsoContribution,
}) => {
  const dsaAddr = ensureAddress(CONTRACT_ADDRESSES.DataServiceAgreement, 'DataServiceAgreement');
  await requireDeployedContract(signer, dsaAddr, 'DataServiceAgreement');
  const contract = getDataServiceAgreementContract(signer);
  const tx = await contract.proposeAgreement(
    dataAssetId,
    serviceId,
    termsHash,
    euBsoContribution
  );
  const receipt = await tx.wait();
  return buildValidatedWriteResult({
    tx,
    receipt,
    contract,
    eventName: 'AgreementProposed',
    requireEvent: true,
    mapEventArgs: (args) => ({ agreementId: Number(args.agreementId) }),
  });
};

export const countersignAgreement = async ({ signer, agreementId }) => {
  const dsaAddr = ensureAddress(CONTRACT_ADDRESSES.DataServiceAgreement, 'DataServiceAgreement');
  await requireDeployedContract(signer, dsaAddr, 'DataServiceAgreement');
  const contract = getDataServiceAgreementContract(signer);
  const tx = await contract.countersign(agreementId);
  const receipt = await tx.wait();
  return { txHash: receipt?.hash || tx.hash };
};

export const activateAgreement = async ({ signer, agreementId }) => {
  const dsaAddr = ensureAddress(CONTRACT_ADDRESSES.DataServiceAgreement, 'DataServiceAgreement');
  await requireDeployedContract(signer, dsaAddr, 'DataServiceAgreement');
  const contract = getDataServiceAgreementContract(signer);
  const tx = await contract.activate(agreementId);
  const receipt = await tx.wait();
  return { txHash: receipt?.hash || tx.hash };
};

export const fulfillAgreement = async ({ signer, agreementId }) => {
  const dsaAddr = ensureAddress(CONTRACT_ADDRESSES.DataServiceAgreement, 'DataServiceAgreement');
  await requireDeployedContract(signer, dsaAddr, 'DataServiceAgreement');
  const contract = getDataServiceAgreementContract(signer);
  const tx = await contract.fulfil(agreementId);
  const receipt = await tx.wait();
  return { txHash: receipt?.hash || tx.hash };
};

export const raiseDispute = async ({ signer, agreementId, reason }) => {
  const dsaAddr = ensureAddress(CONTRACT_ADDRESSES.DataServiceAgreement, 'DataServiceAgreement');
  await requireDeployedContract(signer, dsaAddr, 'DataServiceAgreement');
  const contract = getDataServiceAgreementContract(signer);
  const tx = await contract.raiseDispute(agreementId, reason);
  const receipt = await tx.wait();
  return { txHash: receipt?.hash || tx.hash };
};

export const cancelAgreement = async ({ signer, agreementId }) => {
  const dsaAddr = ensureAddress(CONTRACT_ADDRESSES.DataServiceAgreement, 'DataServiceAgreement');
  await requireDeployedContract(signer, dsaAddr, 'DataServiceAgreement');
  const contract = getDataServiceAgreementContract(signer);
  const tx = await contract.cancel(agreementId);
  const receipt = await tx.wait();
  return { txHash: receipt?.hash || tx.hash };
};

export const getAgreement = async (provider, agreementId) => {
  const dsaAddr = ensureAddress(CONTRACT_ADDRESSES.DataServiceAgreement, 'DataServiceAgreement');
  await requireDeployedContract(provider, dsaAddr, 'DataServiceAgreement');
  const contract = getDataServiceAgreementContract(provider);
  const a = await contract.getAgreement(agreementId);
  return {
    agreementId: Number(a.agreementId),
    consumer: a.consumer,
    provider: a.provider,
    dataAssetId: Number(a.dataAssetId),
    serviceId: Number(a.serviceId),
    escrowAmount: formatUnits(a.escrowAmount, 18),
    dataPrice: formatUnits(a.dataPrice, 18),
    termsHash: a.termsHash,
    status: Number(a.status),
    createdAt: Number(a.createdAt),
    expiresAt: Number(a.expiresAt),
    fulfilledAt: Number(a.fulfilledAt),
    euBsoContribution: Boolean(a.euBsoContribution),
    disputeReason: a.disputeReason,
  };
};
